import React, { useState, useEffect, lazy, Suspense } from 'react';
import { connect } from 'react-redux';
import { ThemeProvider } from 'styled-components';
import { MyThemes } from '../v2/Theme';
import * as c from '../constants';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import PageMeta from './PageMeta';
import ThemeContext from '../contexts/ThemeContext';
import { getTheme } from '../helpers';
import translate from './TranslateHOC';
import Modal from 'react-modal';
import Header from './Header';
import Footer from './Footer';
import AZQuickSearch from './AZQuickSearch';
import FooterSearch from './FooterSearch';
import ConfirmDialog from './mys4c/modals/ConfirmDialog';
import ActionCompleteDialog from './mys4c/modals/ActionCompleteDialog';
import ActionFailDialog from './mys4c/modals/ActionFailDialog';
import MyS4CProfilesSelect from './mys4c/MyS4CProfilesSelect';
import MyS4CForgotPasswordUpdate from './mys4c/MyS4CForgotPasswordUpdate';
import MyS4CVerifyEmail from './mys4c/MyS4CVerifyEmail';
import MyS4CSignUp from './mys4c/MyS4CSignUp';
import RouteLoading from '../v2/components/loading/RouteLoading';
import Programme from './Programme';

// HOCs
import clickUser from './ClicUserHOC';
import myS4CRedirect from './mys4c/MyS4CRedirectHOC';
import { BrowserRouter, Route, Switch } from 'react-router-dom';

/* Lazy load pages */
const Home = lazy(() => import('./Home'));
const MyS4CProfiles = lazy(() => import('./mys4c/MyS4CProfiles'));
const MyS4CEditProfile = lazy(() => import('./mys4c/MyS4CEditProfile'));
const MyS4CWatching = lazy(() => import('./mys4c/MyS4CWatching'));
const MyS4CMyList = lazy(() => import('./mys4c/MyS4CMyList'));
const MyS4CSignIn = lazy(() => import('./mys4c/MyS4CSignIn'));
const MyS4CForgotPassword = lazy(() => import('./mys4c/MyS4CForgotPassword'));
const MyS4CAccount = lazy(() => import('./mys4c/MyS4CAccount'));
const MyS4CDeleteSuccess = lazy(() => import('./mys4c/MyS4CDeleteSuccess'));
const Live = lazy(() => import('./Live'));
const TVSchedule = lazy(() => import('./TVSchedule'));
const Categories = lazy(() => import('./Categories'));
const BoxSets = lazy(() => import('./BoxSets'));
const Search = lazy(() => import('./search/Search'));
const ParentalControl = lazy(() => import('./ParentalControl'));
const TV = lazy(() => import('./TV'));
const TVLink = lazy(() => import('./TVLink'));
const AboutApp = lazy(() => import('./AboutApp'));
const ForgottenPIN = lazy(() => import('./ForgottenPIN'));
const NoMatch = lazy(() => import('./Error404'));

Modal.setAppElement(document.getElementById('root'));

function App(props) {
  const { isAuthenticated } = props;
  const [themeName, setThemeName] = useState(c.DEFAULT_THEME);

  useEffect(() => {
    setThemeName(getTheme());
  }, [setThemeName]);

  useEffect(() => {
    document.documentElement.lang = props.curLang == 0 ? 'cy' : 'en';
  }, [props.curLang]);

  function ClickUserHOC({ component: Component, ...props }) {
    const ClickUserHOCEnhanced = clickUser(Component);
    return <ClickUserHOCEnhanced {...props} />;
  }

  function MyS4CRedirectHOC({ component: Component, ...props }) {
    const MyS4CRedirectEnhanced = myS4CRedirect(Component);
    return <MyS4CRedirectEnhanced {...props} />;
  }

  return (
    <ThemeContext.Provider value={[themeName, setThemeName]}>
      <ThemeProvider theme={MyThemes(themeName)}>
        <BrowserRouter basename={c.PATH_BASE}>
          <div
            className={
              isAuthenticated ||
              !props.pathname === '/tv' ||
              !props.pathname === '/link' ||
              !props.pathname === '/linc'
                ? 'logged-in'
                : 'logged-out'
            }
          >
            <PageMeta />
            <Route
              component={clickUser((props) => (
                <Header {...props} />
              ))}
            />
            <main className="page-content">
              {props.children}

              <Switch>
                <Route exact path="/">
                  <Suspense fallback={<RouteLoading />}>
                    <ClickUserHOC component={Home} />
                  </Suspense>
                </Route>

                <Route
                  path="/programme/:id/:signed"
                  component={clickUser((props) => (
                    <Programme {...props} />
                  ))}
                />
                <Route
                  path="/programme/:id"
                  component={clickUser((props) => (
                    <Programme {...props} />
                  ))}
                />
                <Route
                  path="/series/:series_id"
                  component={(props) => <Programme {...props} />}
                />

                <Route
                  exact
                  path={c.PATH_PROFILES}
                  render={(props) => (
                    <Suspense fallback={<RouteLoading />}>
                      <MyS4CRedirectHOC {...props} component={MyS4CProfiles} />
                    </Suspense>
                  )}
                />
                <Route
                  exact
                  path={c.PATH_PROFILE_EDIT}
                  render={(props) => (
                    <Suspense fallback={<RouteLoading />}>
                      <MyS4CRedirectHOC
                        {...props}
                        component={MyS4CEditProfile}
                      />
                    </Suspense>
                  )}
                />
                <Route
                  exact
                  path={`${c.PATH_PROFILE_EDIT}/add`}
                  render={(props) => (
                    <Suspense fallback={<RouteLoading />}>
                      <MyS4CRedirectHOC
                        {...props}
                        component={MyS4CEditProfile}
                      />
                    </Suspense>
                  )}
                />
                <Route
                  exact
                  path={`${c.PATH_PROFILE_EDIT}/:id`}
                  render={(props) => (
                    <Suspense fallback={<RouteLoading />}>
                      <MyS4CRedirectHOC
                        {...props}
                        component={MyS4CEditProfile}
                      />
                    </Suspense>
                  )}
                />
                <Route
                  exact
                  path={c.PATH_WATCHING}
                  render={(props) => (
                    <Suspense fallback={<RouteLoading />}>
                      <MyS4CRedirectHOC {...props} component={MyS4CWatching} />
                    </Suspense>
                  )}
                />
                <Route
                  exact
                  path={c.PATH_MY_LIST}
                  render={(props) => (
                    <Suspense fallback={<RouteLoading />}>
                      <MyS4CRedirectHOC {...props} component={MyS4CMyList} />
                    </Suspense>
                  )}
                />
                <Route
                  exact
                  path={c.PATH_SIGN_IN}
                  render={(props) => (
                    <Suspense fallback={<RouteLoading />}>
                      <MyS4CSignIn {...props} />
                    </Suspense>
                  )}
                />
                {/* When would this path be matched? */}
                <Route
                  exact
                  path="/MyS4C/SignUp/Profiles"
                  component={myS4CRedirect((props) => (
                    <MyS4CProfilesSelect {...props} />
                  ))}
                />
                <Route
                  exact
                  path="/MyS4C/SignUp"
                  component={clickUser((props) => (
                    <MyS4CSignUp {...props} />
                  ))}
                />
                {/* Legacy, no email verification anymore,
                will leave in just in case */}
                <Route
                  exact
                  path="/MyS4C/VerifyEmail/:email/:verifyCode"
                  component={clickUser((props) => (
                    <MyS4CVerifyEmail {...props} />
                  ))}
                />
                <Route
                  exact
                  path={c.PATH_FORGOT}
                  render={(props) => (
                    <Suspense fallback={<RouteLoading />}>
                      <MyS4CForgotPassword {...props} />
                    </Suspense>
                  )}
                />
                {/* TODO: Look at password udpate */}
                <Route
                  exact
                  path={`${c.PATH_FORGOT_UPDATE}/:email/:verifyCode`}
                  component={clickUser((props) => (
                    <MyS4CForgotPasswordUpdate {...props} />
                  ))}
                />
                <Route
                  path={c.PATH_ACCOUNT}
                  render={(props) => (
                    <Suspense fallback={<RouteLoading />}>
                      <MyS4CRedirectHOC {...props} component={MyS4CAccount} />
                    </Suspense>
                  )}
                />
                <Route
                  path="/Live/:feedUrl"
                  render={(props) => (
                    <Suspense fallback={<RouteLoading />}>
                      <Live {...props} />
                    </Suspense>
                  )}
                />
                <Route
                  path="/Live"
                  render={(props) => (
                    <Suspense fallback={<RouteLoading />}>
                      <Live {...props} />
                    </Suspense>
                  )}
                />
                <Route
                  path="/CatchUp"
                  render={(props) => (
                    <Suspense fallback={<RouteLoading />}>
                      <TVSchedule {...props} />
                    </Suspense>
                  )}
                />
                <Route
                  path="/TVSchedule"
                  render={(props) => (
                    <Suspense fallback={<RouteLoading />}>
                      <TVSchedule {...props} />
                    </Suspense>
                  )}
                />
                <Route
                  path="/Categories"
                  render={(props) => (
                    <Suspense fallback={<RouteLoading />}>
                      <Categories {...props} />
                    </Suspense>
                  )}
                />
                <Route
                  path="/BoxSets"
                  render={(props) => (
                    <Suspense fallback={<RouteLoading />}>
                      <BoxSets {...props} />
                    </Suspense>
                  )}
                />
                <Route
                  path="/Search"
                  render={(props) => (
                    <Suspense fallback={<RouteLoading />}>
                      <Search {...props} />
                    </Suspense>
                  )}
                />
                <Route
                  path={c.PATH_PARENT}
                  render={(props) => (
                    <Suspense fallback={<RouteLoading />}>
                      <MyS4CRedirectHOC
                        {...props}
                        component={ParentalControl}
                      />
                    </Suspense>
                  )}
                />
                <Route
                  path="/tv"
                  render={(props) => (
                    <Suspense fallback={<RouteLoading />}>
                      <ClickUserHOC {...props} component={TV} />
                    </Suspense>
                  )}
                />
                <Route
                  path="/link"
                  render={(props) => (
                    <Suspense fallback={<RouteLoading />}>
                      <ClickUserHOC {...props} component={TVLink} />
                    </Suspense>
                  )}
                />
                <Route
                  path="/linc"
                  render={(props) => (
                    <Suspense fallback={<RouteLoading />}>
                      <ClickUserHOC {...props} component={TVLink} />
                    </Suspense>
                  )}
                />
                <Route
                  path="/AboutApp"
                  render={(props) => (
                    <Suspense fallback={<RouteLoading />}>
                      <AboutApp {...props} />
                    </Suspense>
                  )}
                />
                <Route
                  path={c.PATH_DELETE_SUCCESS}
                  render={(props) => (
                    <Suspense fallback={<RouteLoading />}>
                      <MyS4CDeleteSuccess {...props} />
                    </Suspense>
                  )}
                />
                <Route
                  path={c.PATH_PARENT_FORGOT_PIN}
                  render={(props) => (
                    <Suspense fallback={<RouteLoading />}>
                      <MyS4CRedirectHOC {...props} component={ForgottenPIN} />
                    </Suspense>
                  )}
                />
                <Route
                  path="*"
                  status={404}
                  render={(props) => (
                    <Suspense fallback={<RouteLoading />}>
                      <NoMatch {...props} />
                    </Suspense>
                  )}
                />
              </Switch>

              <Switch>
                <Route path="/MyS4C" render={null} />
                <Route path="/MyS4C/Account/ParentalControl" render={null} />
                <Route path="/ForgottenPIN" render={null} />
                <Route path="/tv" render={null} />
                <Route path="/link" render={null} />
                <Route path="/linc" render={null} />
                <Route component={(props) => <AZQuickSearch {...props} />} />
              </Switch>
              <ConfirmDialog />
              <ActionCompleteDialog />
              <ActionFailDialog />
              <Route component={(props) => <FooterSearch {...props} />} />
            </main>
            <Route component={(props) => <Footer {...props} />} />
            <ToastContainer autoClose={8000} />
          </div>
        </BrowserRouter>
      </ThemeProvider>
    </ThemeContext.Provider>
  );
}

const mapStateToProps = (state) => {
  return {
    curLang: state.curLang,
  };
};
export default translate(connect(mapStateToProps)(clickUser(App)));
